import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { track } from "@/client/analytics/mixpanel";
import { axios } from "@/client/libs/axios";
import { useAuthStore } from "@/client/stores/auth";

/**
 * Global payment confirmation handler
 * Handles Stripe redirect with session_id parameter on any page
 */
export const PaymentConfirmationHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const sessionId = search.get("session_id");

    // Only process if we have a session_id and haven't already processed it
    if (!sessionId || isProcessing) return;
    // First Round pass checkouts (?checkout=done) are confirmed by the plan prompts instead.
    if (search.has("checkout")) return;

    const confirmPayment = async () => {
      setIsProcessing(true);
      try {
        const response = await axios.post("/stripe/confirm-payment", { sessionId });

        if (response.data.success) {
          // The payment itself (payment_completed) is sent by the server from Stripe's webhook.
          track("checkout_returned", { status: "paid", flow: "legacy" });
          // Remove session_id and add success indicator
          const params = new URLSearchParams(location.search);
          params.delete("session_id");
          params.set("payment", "success");

          // Stay on current page
          navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        }
      } catch (error) {
        console.error("Payment confirmation failed:", error);
        track("checkout_returned", { status: "failed", flow: "legacy" });
        // Remove session_id but indicate failure
        const params = new URLSearchParams(location.search);
        params.delete("session_id");
        params.set("payment", "failed");

        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    confirmPayment();
  }, [location.search, location.pathname, navigate, isProcessing]);

  return null; // This is a side-effect only component
};
